// @flow

import _ from 'lodash';

import { stripTrailingSlash } from '@zapier/url-utils';

import buildEntityActions from 'app/entity/utils/buildEntityActions';
import buildEntityApi from 'app/entity/utils/buildEntityApi';
import buildEntitySelectors from 'app/entity/utils/buildEntitySelectors';
import { partialWithDefault } from 'app/common/CommonUtils';
import {
  cleanDefinition,
  currentAppDefinitionEntityId,
  currentAppId,
  currentAppVersion,
} from 'app/developer-v3/selectors';

export const type = 'cliAppDefinition';

export const api = buildEntityApi(type, (path, getState) => {
  const state = getState();
  const appId = currentAppId(state);

  const versionNumber = currentAppVersion(state);
  const isCollection = path === '';
  const platformVersion = _.get(cleanDefinition, ['platformVersion']);
  const finalPathSegment = isCollection
    ? ''
    : platformVersion
    ? `${versionNumber}?schema_version=${platformVersion}`
    : `${versionNumber}`;

  // TODO: In the previous commit we were appending `path`, which contained the
  // CliAppDefinition `id`, to the end of the URl path below, but that was
  // breaking CliAppDefinition loading and saving. The current solution either
  // appends '' or a version number, e.g., "1.0.0", which fixes loading and
  // saving, but is inconsistent with adjustments we would like to make to the
  // backend API. In the future, the API will accept the CliAppDefintion `id`
  // as the final path segment, i.e., we can revert the commit which introduced
  // this comment.
  return stripTrailingSlash(
    `/api/platform/cli/apps/${appId}/versions/${finalPathSegment}`
  );
});

export const selectors = buildEntitySelectors(type, sel => {
  sel.definitionOverride = partialWithDefault(
    sel.dataItem,
    ['definition_override'],
    {}
  );
  sel.etag = _.partial(sel.dataItem, 'etag');
  sel.appId = _.partial(sel.dataItem, 'app_id');
  sel.version = _.partial(sel.dataItem, 'version');
  sel.selectedApi = _.partial(sel.dataItem, 'selected_api');
  sel.deployment = _.partial(sel.dataItem, 'deployment');
  sel.deprecationDate = _.partial(sel.dataItem, 'deprecation_date');
  sel.userCount = _.partial(sel.dataItem, 'user_count');
  sel.oauthRedirectUri = _.partial(sel.dataItem, 'oauth_redirect_uri');
  sel.isUi = _.partial(sel.dataItem, 'is_ui');
  sel.auth = _.partial(sel.dataItem, ['definition_override', 'authentication']);
  sel.authFields = _.partial(sel.dataItem, [
    'definition_override',
    'authentication',
    'fields',
  ]);
  sel.authType = _.partial(sel.dataItem, [
    'definition_override',
    'authentication',
    'type',
  ]);
  sel.isOauth2 = partialWithDefault(
    sel.matchesSelector,
    [sel.authType, 'oauth2'],
    false
  );

  const hasAppIdAndVersion = (appId, version, entity) => {
    return (
      sel.appId(entity) === Number(appId) && sel.version(entity) === version
    );
  };
  sel.all.findByAppIdAndVersion = _.partial(sel.all.findBy, hasAppIdAndVersion);
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  cliAppDefinitionActions => {
    cliAppDefinitionActions.updateAppDefinition = (
      definition_override,
      etag,
      messages = {}
    ) => {
      return async (dispatch, getState) => {
        const state = getState();
        const entityId = currentAppDefinitionEntityId(state);

        dispatch(
          cliAppDefinitionActions.updateEntityData(
            {
              definition_override,
              ...(etag && { etag }),
            },
            entityId
          )
        );

        return await dispatch(
          cliAppDefinitionActions.saveEntity(messages, entityId)
        );
      };
    };
  }
);
